import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormSubmission } from '../src/hooks/useFormSubmission';
import { useCodeValidation } from '../src/hooks/useCodeValidation';
import { useLookupData } from '../src/hooks/useLookupData';
import { SubmitFormData } from '../src/service/apiService';
import npaLogo from './assets/npaworldwide.png';
import rrlogo from './assets/rolerelay.svg'

// Zod schema for form validation
const profileSchema = z.object({
  code: z.string().min(1, 'Access code is required'),
  email: z.string().email('Please enter a valid email address'),
  mobile_phone: z.string().min(1, 'Mobile phone is required'),
  first_name: z.string().min(1, 'First name is required'),
  family_name: z.string().min(1, 'Family name is required'),
  company_name: z.string().min(1, 'Company name is required'),
  ats_system_id: z.number().nullable(),
  ats_other: z.string().optional(),
  industry_ids: z.array(z.number()).min(1, 'Please select at least one industry'),
  region_ids: z.array(z.number()).min(1, 'Please select at least one region'),
  locality_ids: z.array(z.number()).min(1, 'Please select at least one locality'),
  privacy_consent: z.boolean().refine(val => val === true, 'Privacy consent is required'),
  marketing_consent: z.boolean().refine(val => val === true, 'Privacy consent is required'),
  terms_accepted: z.boolean().refine(val => val === true, 'Terms acceptance is required'),
  // Single consent field for UI
  all_consents: z.boolean().refine(val => val === true, 'You must accept all terms and conditions'),
}).refine(
  (data) => {
    if (data.ats_system_id === null && !data.ats_other) {
      return false;
    }
    return true;
  },
  {
    message: "Please specify your ATS system",
    path: ["ats_other"],
  }
);

type ProfileFormData = z.infer<typeof profileSchema>;

// Utility to get URL parameters - memoized to prevent recreating
const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export function App(): JSX.Element {
  // Get code from URL parameter - memoized to prevent recreation
  const codeFromUrl = useMemo(() => getUrlParameter('code') || '', []);

    // Remove code from URL bar after reading it
  useEffect(() => {
    if (codeFromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, '', url.toString());
    }
  }, [codeFromUrl]);
  
  // Hooks for API interactions
  const { 
    isValidating, 
    validationResult, 
    validationError, 
    validateCode,  
  } = useCodeValidation();
  
  const {
    isSubmitting,
    submitError,
    submitSuccess,
    submitResponse,
    submitForm
  } = useFormSubmission();
  
  const {
    industries,
    regions,
    localities,
    ats_systems,
    isLoading: isLoadingLookups,
    error: lookupsError
  } = useLookupData();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    setError,
    trigger, // Add trigger for manual validation
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      code: codeFromUrl,
      email: '',
      mobile_phone: '',
      first_name: '',
      family_name: '',
      company_name: '',
      ats_system_id: null,
      ats_other: '',
      industry_ids: [],
      region_ids: [],
      locality_ids: [],
      privacy_consent: false,
      marketing_consent: false,
      terms_accepted: false,
      all_consents: false,
    },
    mode: 'onBlur', // Changed from 'onChange' to prevent excessive validation
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  // Local state
  const [codeValidated, setCodeValidated] = useState(false);
  const [isAtsOpen, setIsAtsOpen] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);


useEffect(() => {
  let timer: NodeJS.Timeout;
  
  if (isLoadingLookups || !codeValidated) {
    setShowLoadingSpinner(true);
  } else {
    timer = setTimeout(() => {
      setShowLoadingSpinner(false);
    }, 5000);
  }
  
  return () => {
    if (timer) clearTimeout(timer);
  };
}, [isLoadingLookups, codeValidated]);

  const styles = useMemo(() => ({
    inputBase: 'w-full px-3 py-3 border rounded-md focus:outline-none focus:ring-2 transition-colors',
    inputValid: 'border-gray-300 focus:ring-customTeal-light focus:border-customTeal-light',
    inputError: 'border-red-500 focus:ring-red-300 focus:border-red-500',
    checkbox: 'w-4 h-4 text-customTeal-light border-gray-300 rounded focus:ring-customTeal-light focus:ring-2',
  }), []);

  // Watch specific values
  const watchCode = watch('code');
  const watchEmail = watch('email');
  const watchRegionIds = watch('region_ids');
  const watchLocalityIds = watch('locality_ids');
  const watchIndustryIds = watch('industry_ids');
  const watchAtsSystemId = watch('ats_system_id');
  const watchAtsOther = watch('ats_other');
  const watchAllConsents = watch('all_consents');

  // Handle single consent checkbox change
  useEffect(() => {
    if (watchAllConsents) {
      setValue('privacy_consent', true, { shouldValidate: true });
      setValue('marketing_consent', true, { shouldValidate: true });
      setValue('terms_accepted', true, { shouldValidate: true });
    } else {
      setValue('privacy_consent', false, { shouldValidate: true });
      setValue('marketing_consent', false, { shouldValidate: true });
      setValue('terms_accepted', false, { shouldValidate: true });
    }
  }, [watchAllConsents, setValue]);

  // Memoize filtered localities to prevent unnecessary recalculations
  const filteredLocalities = useMemo(() => {
    if (watchRegionIds.length > 0) {
      return localities.filter(locality => 
        locality.region_id && watchRegionIds.includes(locality.region_id)
      );
    }
    return localities;
  }, [localities, watchRegionIds]);


  const handleCodeValidation = useCallback(async () => {
    if (watchCode && !codeValidated) {
      await validateCode(watchCode, watchEmail || undefined);
    }
  }, [watchCode, watchEmail, codeValidated, validateCode]);

  // Validate code on component mount or code change
  useEffect(() => {
    if (watchCode && !isValidating) {
      handleCodeValidation();
    }
  }, [watchCode]);

  // Update code validation status
  useEffect(() => {
    if (validationResult?.valid && !codeValidated) {
      setCodeValidated(true);
      // If code has associated email, pre-fill it only if email is empty
      if (validationResult.email && !watchEmail) {
        setValue('email', validationResult.email, { shouldValidate: true, shouldTouch: true });
      }
    } else if (validationResult && !validationResult.valid && codeValidated) {
      setCodeValidated(false);
    }
  }, [validationResult, codeValidated, watchEmail, setValue]);

  // Handle locality filtering with proper validation trigger
  useEffect(() => {
    if (watchRegionIds.length > 0) {
      const validLocalityIds = watchLocalityIds.filter(id => 
        filteredLocalities.some(loc => loc.id === id)
      );
      
      if (validLocalityIds.length !== watchLocalityIds.length) {
        setValue('locality_ids', validLocalityIds, { shouldValidate: true, shouldTouch: true });
        // Manually trigger validation
        trigger('locality_ids');
      }
    } else if (watchLocalityIds.length > 0) {
      setValue('locality_ids', [], { shouldValidate: true, shouldTouch: true });
      trigger('locality_ids');
    }
  }, [watchRegionIds, filteredLocalities, watchLocalityIds, setValue, trigger]);

  // Simplified event handlers with proper validation
  const handleIndustryChange = useCallback((industryId: number, checked: boolean) => {
    const currentIds = watchIndustryIds;
    const newIds = checked 
      ? [...currentIds, industryId]
      : currentIds.filter(id => id !== industryId);
    setValue('industry_ids', newIds, { shouldValidate: true, shouldTouch: true });
    trigger('industry_ids'); // Manual validation trigger
  }, [watchIndustryIds, setValue, trigger]);

  const handleRegionChange = useCallback((regionId: number, checked: boolean) => {
    const currentIds = watchRegionIds;
    const newIds = checked 
      ? [...currentIds, regionId]
      : currentIds.filter(id => id !== regionId);
    setValue('region_ids', newIds, { shouldValidate: true, shouldTouch: true });
    trigger('region_ids'); // Manual validation trigger
  }, [watchRegionIds, setValue, trigger]);

  const handleLocalityToggle = useCallback((localityId: number) => {
    const currentIds = watchLocalityIds;
    const newIds = currentIds.includes(localityId)
      ? currentIds.filter(id => id !== localityId)
      : [...currentIds, localityId];
    setValue('locality_ids', newIds, { shouldValidate: true, shouldTouch: true });
    trigger('locality_ids'); // Manual validation trigger
  }, [watchLocalityIds, setValue, trigger]);

  const handleAtsSelection = useCallback((atsId: number | null) => {
    setValue('ats_system_id', atsId, { shouldValidate: true, shouldTouch: true });
    if (atsId !== null) {
      setValue('ats_other', '', { shouldValidate: true, shouldTouch: true });
    }
    setIsAtsOpen(false);
    // Trigger validation for both fields
    trigger(['ats_system_id', 'ats_other']);
  }, [setValue, trigger]);

  const onSubmit = useCallback(async (data: ProfileFormData) => {
    if (!codeValidated) {
      setError('code', { type: 'manual', message: 'Please validate your access code first' });
      return;
    }

    const submissionData: SubmitFormData = {
      code: data.code,
      email: data.email,
      mobile_phone: data.mobile_phone,
      first_name: data.first_name,
      family_name: data.family_name,
      company_name: data.company_name,
      ats_system_id: data.ats_system_id || undefined,
      ats_other: data.ats_other || undefined,
      industry_ids: data.industry_ids,
      region_ids: data.region_ids,
      locality_ids: data.locality_ids,
      privacy_consent: data.privacy_consent,
      marketing_consent: data.marketing_consent,
      terms_accepted: data.terms_accepted,
    };

    try {
      await submitForm(submissionData);
      reset();
    } catch (error) {
      // Error handling is done in the hook
    }
  }, [codeValidated, submitForm, reset, setError]);

  // Compute derived styles to prevent recreation
  const getInputStyles = useCallback((hasError: boolean) => 
    `${styles.inputBase} ${hasError ? styles.inputError : styles.inputValid}`, [styles]);

  const getSelectStyles = useCallback((hasError: boolean) => 
    `${getInputStyles(hasError)} bg-white appearance-none`, [getInputStyles]);

  // Show success screen
  if (submitSuccess && submitResponse) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-green-600 text-2xl font-bold">✓</span>
              </div>
              <p className="text-gray-600 mb-6">Thank you for providing your details. <br />
                 A member of the Role Relay team will be in touch with you shortly to arrange onboarding and getting you started.</p>
            
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show warning if code is invalid after validation attempt
  if (validationError && !isValidating && !isLoadingLookups) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-600 text-2xl font-bold">!</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Access Code</h2>
              <p className="text-gray-600 mb-8">
                The access code you provided is invalid or has expired. Please contact{' '}
                <a href="mailto:support@rolerelay.com" className="text-blue-600 hover:text-blue-800">
                  support@rolerelay.com
                </a>{' '}
                for a new invitation code.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

    // Show warning if no access code in URL
  if (!codeFromUrl && !isLoadingLookups) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-600 text-2xl font-bold">!</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Code Required</h2>
              <p className="text-gray-600 mb-8">
                This form is accessed by invited users only. Please contact{' '}
                <a href="mailto:support@rolerelay.com" className="text-blue-600 hover:text-blue-800">
                  support@rolerelay.com
                </a>{' '}
                for more info.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading screen with delayed spinner
  if (isLoadingLookups) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          {showLoadingSpinner && (
            <>
              <div className="animate-spin h-8 w-8 border-b-2 border-customTeal rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading form data...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Show error if lookup data failed to load
  if (lookupsError) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-600 text-2xl font-bold">!</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Form</h2>
              <p className="text-gray-600 mb-8">{lookupsError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center space-x-6 mb-6">
            <img src={npaLogo} alt="NPAworldwide" className="h-18 w-auto max-w-[250px]" />
            <img src={rrlogo} alt="RoleRelay" className="h-18 w-auto max-w-[250px]" />
          </div>
         
          <p className="text-gray-600">Thank you for partaking in the NPAworldwide Role Relay pilot. 
            Please provide your details below for us to setup your account.</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Form Status Messages */}
            {submitError && (
              <div className="p-4 bg-red-100 text-red-700 rounded-md">
                {submitError}
              </div>
            )}

            {/* Access Code Section */}
            

            {/* Only show form fields if code is validated */}
            {codeValidated && (
              <>
                {/* Personal Details */}
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-customTeal-dark text-sm font-medium">1</span>
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">Personal Details</h2>
                  </div>

                  <div className="ml-9 space-y-4">
                    <div className="relative">
                      <input
                        {...register('email')}
                        type="email"
                        disabled
                        className={`${getInputStyles(!!errors.email)} bg-gray-100 cursor-not-allowed`}
                        placeholder="john.smith@example.com"
                      />
                      {watchEmail && !errors.email && (
                        <div className="absolute right-3 top-3">
                          <span className="text-green-600 text-sm font-medium">Verified</span>
                        </div>
                      )}
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                    </div>

                    <div>
                      <input
                        {...register('mobile_phone')}
                        type="tel"
                        className={getInputStyles(!!errors.mobile_phone)}
                        placeholder="+61 400 123 456"
                      />
                      {errors.mobile_phone && <p className="text-red-500 text-sm mt-1">{errors.mobile_phone.message}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          {...register('first_name')}
                          type="text"
                          className={getInputStyles(!!errors.first_name)}
                          placeholder="John"
                        />
                        {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name.message}</p>}
                      </div>
                      <div>
                        <input
                          {...register('family_name')}
                          type="text"
                          className={getInputStyles(!!errors.family_name)}
                          placeholder="Smith"
                        />
                        {errors.family_name && <p className="text-red-500 text-sm mt-1">{errors.family_name.message}</p>}
                      </div>
                    </div>

                    <div>
                      <input
                        {...register('company_name')}
                        type="text"
                        className={getInputStyles(!!errors.company_name)}
                        placeholder="Company/Organisation"
                      />
                      {errors.company_name && <p className="text-red-500 text-sm mt-1">{errors.company_name.message}</p>}
                    </div>
                  </div>
                </div>

                {/* ATS Platform */}
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-6 h-6 bg-[#E6F3F3] rounded-full flex items-center justify-center mr-3">
                      <span className="text-customTeal-dark text-sm font-medium">2</span>
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">ATS Platform Used</h2>
                  </div>
                  
                  <div className="ml-9">
                    <div className="relative">
                      <div
                        className={`${getSelectStyles(!!errors.ats_system_id || !!errors.ats_other)} relative pr-10 text-left cursor-pointer`}
                        onClick={() => setIsAtsOpen(!isAtsOpen)}
                      >
                        <span className={watchAtsSystemId || watchAtsOther ? 'text-gray-900' : 'text-gray-500'}>
                          {watchAtsSystemId 
                            ? ats_systems.find(ats => ats.id === watchAtsSystemId)?.name
                            : watchAtsOther ? 'Other (Custom)'
                            : 'Select ATS Platform'
                          }
                        </span>
                      </div>
                      {isAtsOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto max-h-60">
                          {ats_systems.map((ats) => (
                            <div
                              key={ats.id}
                              className={`cursor-pointer select-none py-2 pl-3 pr-9 ${
                                watchAtsSystemId === ats.id
                                  ? 'bg-customTeal-dark text-gray-900 font-medium'
                                  : 'text-gray-700 hover:bg-customTeal-dark hover:text-white'
                              }`}
                              onClick={() => handleAtsSelection(ats.id)}
                            >
                              <span>{ats.name}</span>
                            </div>
                          ))}
                          <div
                            className={`cursor-pointer select-none py-2 pl-3 pr-9 ${
                              watchAtsSystemId === null && watchAtsOther
                                ? 'bg-customTeal-dark text-gray-900 font-medium'
                                : 'text-gray-700 hover:bg-customTeal-dark hover:text-white'
                            }`}
                            onClick={() => handleAtsSelection(null)}
                          >
                            <span>Other</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {(errors.ats_system_id || errors.ats_other) && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.ats_system_id?.message || errors.ats_other?.message}
                      </p>
                    )}
                    
                    {watchAtsSystemId === null && (
                      <div className="mt-4">
                        <input
                          {...register('ats_other')}
                          type="text"
                          placeholder="Enter your ATS platform"
                          className={getInputStyles(!!errors.ats_other)}
                        />
                        {errors.ats_other && <p className="text-red-500 text-sm mt-1">{errors.ats_other.message}</p>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Operating Regions */}
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-customTeal-dark text-sm font-medium">3</span>
                    </div>
                    <h2 className="text-lg font-medium text-gray-900">Operating Region *</h2>
                  </div>

                  <div className="ml-9 space-y-3">
                    {regions.map(region => (
                      <label key={region.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={watchRegionIds.includes(region.id)}
                          onChange={(e) => handleRegionChange(region.id, e.target.checked)}
                          className={styles.checkbox}
                        />
                        <span className="ml-3 text-gray-700">{region.name}</span>
                      </label>
                    ))}
                    {errors.region_ids && <p className="text-red-500 text-sm mt-1">{errors.region_ids.message}</p>}
                  </div>
                </div>

                 {/* Service Locations */}
                {watchRegionIds.length > 0 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-customTeal-dark text-sm font-medium">5</span>
                      </div>
                      <h2 className="text-lg font-medium text-gray-900">Service Locations *{' '}<span className="text-sm text-gray-500">({watchLocalityIds.length} selected)</span></h2>
                    </div>
                    <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto bg-gray-50">
                      {filteredLocalities.map(locality => (
                        <div
                          key={locality.id}
                          onClick={() => handleLocalityToggle(locality.id)}
                          className={`px-4 py-3 cursor-pointer border-b border-gray-200 last:border-b-0 hover:bg-gray-100 ${
                            watchLocalityIds.includes(locality.id)
                              ? 'bg-customTeal-dark text-white hover:text-customTeal-dark font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          {locality.name} ({locality.type})
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">Click to select multiple locations</p>
                    {errors.locality_ids && <p className="text-red-500 text-sm mt-1">{errors.locality_ids.message}</p>}
                  </div>
                )}

                {/* Target Industries */}
                <div>
                  <div className="flex items-center mb-6">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-customTeal-dark text-sm font-medium">4</span>
                      </div>
                      <h2 className="text-lg font-medium text-gray-900">Target Industries *{' '}<span className="text-sm text-gray-500">({watchIndustryIds.length} selected)</span></h2>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {industries.map(industry => (
                      <label key={industry.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={watchIndustryIds.includes(industry.id)}
                          onChange={(e) => handleIndustryChange(industry.id, e.target.checked)}
                          className={styles.checkbox}
                        />
                        <span className="ml-3 text-gray-700">{industry.name}</span>
                      </label>
                    ))}
                  </div>
                  {errors.industry_ids && <p className="text-red-500 text-sm mt-1">{errors.industry_ids.message}</p>}
                </div>

               

                {/* Single Consent Checkbox */}
                <section>
                  <div className="space-y-4">
                      <label className="flex items-start">
                        <input
                          {...register('all_consents')}
                          type="checkbox"
                          className={styles.checkbox}
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          <strong>Terms & Conditions *</strong><br />
                          I agree to the {' '}
                          <a target="_blank" href="https://platform.rolerelay.com/static/media/role_relay_terms.fe313b6b337acbf8ae90.pdf" className="text-blue-600 underline hover:text-blue-800">
                            Terms and Conditions
                          </a>{', '}  and consent to data processing as per the {' '}
                          <a target="_blank" href="https://platform.rolerelay.com/privacy" className="text-blue-600 underline hover:text-blue-800">
                            Privacy Policy
                          </a>{' '}
                          to create my RoleRelay profile.
                        </span>
                      </label>
                      {errors.all_consents && <p className="text-red-500 text-sm mt-1">{errors.all_consents.message}</p>}
                </div>
                </section>

                {/* Submit Button */}
                <div className="relative">
                  <button
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    className={`w-full py-4 px-6 rounded-md font-medium text-lg text-white transition-colors ${
                      isSubmitting || !isValid
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-customTeal hover:bg-customTeal-dark focus:outline-none focus:ring-2 focus:ring-customTeal-light focus:ring-offset-2'
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full mr-2"></div>
                        Submitting...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>

                <p className="text-center text-sm text-gray-500">
                  Questions? Email{' '}
                  <a href="mailto:support@rolerelay.com" className="text-blue-600 hover:text-blue-800">
                    support@rolerelay.com
                  </a>
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default App;